/**
 * 发版更新提示
 *
 * 判断依据始终是版本号：拉一次 index.html，把构建时注入的 <meta name="app-build">
 * （构建时间戳，见 vite.config.ts 的 inject-build-version 插件）与当前页面里的比对，
 * 不一致即为已发新版，在右下角弹常驻通知引导用户手动刷新。
 *
 * 何时去比对分两种时机：
 * - 被动：老页面点懒加载路由会请求到已被覆盖的旧 chunk（404，表现为白屏或「点了没反应」），
 *   捕获这类加载失败后立即比对一次。它只是触发时机、不是判断依据——断网同样会加载失败，
 *   得靠版本号比对区分开。好处是用户真撞上问题的那一刻就能提示，不必等轮询周期；
 *   生产包不开轮询，只有这三条信号兜底。
 * - 主动：测试环境每 3 分钟比对一次。产品 / 测试常停在同一个页面上验证，不点懒加载路由
 *   就触发不了被动信号，容易在早已修好的旧版本上复现出「bug」。
 *
 * 与 src/monitor 相互独立：监控负责把错误上报给开发，这里负责提示用户，
 * 监控总开关关闭时本提示仍需工作1。
 */
import { h } from 'vue';
import { ElButton, ElNotification } from 'element-plus';
import { RefreshRight } from '@element-plus/icons-vue';
import type { NotificationHandle } from 'element-plus';
import type { Router } from 'vue-router';

/** 测试环境主动轮询间隔 */
const POLL_INTERVAL_MS = 3 * 60 * 1000;

/** 两次轮询的最小间隔：切回前台会立即补查一次，防止频繁切标签页连打请求 */
const MIN_POLL_GAP_MS = 30 * 1000;

/** 路由懒加载 import 失败的错误文案，各浏览器措辞不同 */
const DYNAMIC_IMPORT_ERROR =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

/** 通知正在展示 */
let notifying = false;
/** 版本确认请求进行中，避免同一次发版并发触发多个请求 */
let checking = false;
/** 用户点「稍后」关掉的版本标记：同一版本不再打扰，直到下一次发版 */
let dismissedBuild = '';
/** 上次轮询时间戳，配合 MIN_POLL_GAP_MS 限流 */
let lastPolledAt = 0;

/** 是否本站自己的构建产物：第三方 SDK 脚本加载失败与发版无关，不能算 */
const isOwnAsset = (url: string): boolean => {
  const { origin, pathname } = new URL(url, location.href);
  return origin === location.origin && pathname.startsWith(`${import.meta.env.BASE_URL}assets/`);
};

/** 读 index.html 中构建时注入的版本标记（见 vite.config.ts 的 inject-build-version 插件） */
const parseBuildTag = (html: string): string =>
  html.match(/<meta\s+name="app-build"\s+content="([^"]*)"/i)?.[1] ?? '';

/**
 * 拉一次 index.html 比对版本标记，确认是「发版了」而不是「网络断了」。
 * 返回远程的新版本标记；无新版本、或无从比对时返回空串。
 *
 * 有意例外（CLAUDE.md「非必要不加 try/catch」）：此处正是要区分请求成败——
 * 断网时 fetch 抛错必须吞掉并判为「非新版本」，否则会把网络故障误报成发版。
 */
const fetchNewBuild = async (): Promise<string> => {
  const current = document.querySelector<HTMLMetaElement>('meta[name="app-build"]')?.content ?? '';
  /** 没有版本标记说明不是正式构建产物，无从比对 */
  if (!current) return '';
  try {
    /** 带时间戳：no-store 只能绕开浏览器缓存，query 参数才能绕开 nginx / CDN 的缓存副本 */
    const res = await fetch(`${import.meta.env.BASE_URL}index.html?_=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return '';
    const remote = parseBuildTag(await res.text());
    return remote && remote !== current ? remote : '';
  } catch {
    return '';
  }
};

const showNotice = (build: string): void => {
  notifying = true;
  /** 闭包内的按钮回调执行时 instance 已完成赋值 */
  const instance: NotificationHandle = ElNotification({
    title: '系统已更新到新版本',
    /** 用刷新图标指向要做的动作，替掉 type 自带的灰色感叹号 */
    icon: RefreshRight,
    /** 样式见 src/styles/element-overrides.scss 的 .app-update-notice */
    customClass: 'app-update-notice',
    position: 'bottom-right',
    /** 关闭入口只留「稍后」，右上角 × 与它重复 */
    showClose: false,
    /** 常驻不自动消失：用户可能正在操作，由他自己决定何时刷新 */
    duration: 0,
    message: h('div', [
      h('div', '为避免页面功能异常，请刷新后继续使用。'),
      h('div', { class: 'app-update-notice__actions' }, [
        h(ElButton, { size: 'small', onClick: () => instance.close() }, () => '稍后'),
        h(
          ElButton,
          { size: 'small', type: 'primary', onClick: () => location.reload() },
          () => '立即刷新',
        ),
      ]),
    ]),
    onClose: () => {
      notifying = false;
      /** 记下这一版：用户已知晓，不再重复打扰，等下一次发版再提示 */
      dismissedBuild = build;
    },
  });
};

/** 查一次版本，确有新版且用户没关过它才提示 */
const checkAndNotify = async (): Promise<void> => {
  if (notifying || checking) return;
  checking = true;
  const build = await fetchNewBuild();
  checking = false;
  if (build && build !== dismissedBuild) showNotice(build);
};

/** 一次轮询：后台标签页不查，距上次太近也不查 */
const poll = (): void => {
  if (document.hidden || Date.now() - lastPolledAt < MIN_POLL_GAP_MS) return;
  lastPolledAt = Date.now();
  void checkAndNotify();
};

/**
 * 注册发版更新提示。
 *
 * 本地 dev server 不启用：首次进入懒加载路由要现场编译 chunk，
 * HMR 与编译期的 404 会造成误报（同 src/monitor 的处理）。
 */
export const setupAppUpdateNotice = (router: Router): void => {
  if (__IS_DEV_SERVER__) return;

  /** 1. script / link 标签 404。资源错误不冒泡，必须在捕获阶段监听 */
  window.addEventListener(
    'error',
    (event: Event) => {
      const target = event.target;
      const url =
        target instanceof HTMLScriptElement
          ? target.src
          : target instanceof HTMLLinkElement
            ? target.href
            : '';
      if (url && isOwnAsset(url)) void checkAndNotify();
    },
    true,
  );

  /** 2. Vite 动态 import 预加载失败：老页面点新路由时最准的信号 */
  window.addEventListener('vite:preloadError', () => {
    void checkAndNotify();
  });

  /** 3. 路由懒加载 import 失败兜底 */
  router.onError((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);
    if (DYNAMIC_IMPORT_ERROR.test(message)) void checkAndNotify();
  });

  /**
   * 4. 主动轮询：只给测试环境（build:dev 产物）开，生产包 MODE 为 production 不进这里，
   * 靠上面三条被动信号即可。切回前台立即补查一次，省得后台跳过的轮询要等下一个周期。
   */
  if (import.meta.env.MODE === 'development') {
    setInterval(poll, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', poll);
  }
};
